<template>
  <div
    v-if="form.origemMeta === 2"
    class="mb-3"
    style="border: 1px solid rgba(25,118,210,0.3); border-radius: 8px; padding: 12px 14px; background: rgba(25,118,210,0.03)"
  >
    <p class="caption font-weight-bold text-uppercase ma-0 mb-3" style="letter-spacing: 0.05em; font-size: 10px; color: #1976d2">Parâmetros da Média</p>

    <p class="caption ma-0 mb-3" style="line-height: 1.4">
      <strong>Base da média:</strong> {{ textoBaseMedia }}
    </p>

    <v-select
      :value="form.mesesReferencia"
      @change="setMeses"
      :items="opcoesMeses"
      item-text="label"
      item-value="valor"
      label="Meses de referência"
      multiple
      chips
      small-chips
      dense
      outlined
      hide-details
      class="mb-3"
    ></v-select>

    <v-text-field
      :value="form.valorMinimoMeta"
      @input="setParametro('valorMinimoMeta', $event)"
      @blur="pisoTocado = true"
      label="Meta piso"
      type="number"
      dense
      outlined
      class="mb-3"
      :hide-details="!(pisoTocado && erroPiso)"
      :error-messages="pisoTocado ? erroPiso : ''"
    ></v-text-field>

    <p class="caption font-weight-bold text-uppercase ma-0 mb-2" style="letter-spacing: 0.05em; font-size: 10px; color: rgba(0,0,0,0.7)">Tipo de Crescimento</p>
    <div class="d-flex mb-3" style="gap: 6px">
      <button
        v-for="opt in tiposCrescimento"
        :key="opt.value"
        type="button"
        @click="setTipoCrescimento(opt.value)"
        class="rounded pa-2 text-center caption font-weight-medium flex-grow-1"
        style="cursor: pointer; border: 2px solid; transition: all 0.2s; font-family: inherit; outline: none; -webkit-appearance: none; appearance: none"
        :style="tipoCrescimentoAtivo === opt.value ? 'border-color: #1976d2; background: rgba(25,118,210,0.06); color: #1976d2' : 'border-color: rgba(0,0,0,0.12); background: white; color: rgba(0,0,0,0.6)'"
      >{{ opt.label }}</button>
    </div>

    <v-text-field
      v-if="tipoCrescimentoAtivo === 1"
      :value="form.percentualCrescimento"
      @input="setParametro('percentualCrescimento', $event)"
      label="Crescimento esperado"
      suffix="%"
      type="number"
      dense
      outlined
      hide-details
    ></v-text-field>

    <div v-else>
      <div class="d-flex align-center mb-2" style="gap: 10px">
        <v-btn small outlined color="primary" @click="modalFaixasAberto = true">
          <v-icon small class="mr-1">mdi-stairs-box</v-icon>Configurar faixas
        </v-btn>
        <span v-if="pendenciasFaixas.length" class="caption orange--text text--darken-2">{{ resumoFaixas }}</span>
      </div>

      <!-- Resposta, nao pergunta: a configuracao fica visivel aqui, sem exigir
           abrir o modal so para conferir o que ja foi definido. O modal segue
           existindo so para EDITAR.

           Mostra o intervalo EFETIVO (calcularFaixasEfetivas), nao o "ate"
           digitado como rotulo — sao numeros diferentes de proposito (ver
           faixasCrescimentoUtils.js), e e exatamente essa diferenca que
           precisa ficar visivel pra nao confundir quem esta cadastrando. -->
      <div v-if="!pendenciasFaixas.length" style="border: 1px solid rgba(0,0,0,0.12); border-radius: 6px; overflow: hidden">
        <div
          v-for="(faixa, idx) in faixasEfetivas"
          :key="idx"
          class="d-flex align-center caption px-3 py-1"
          :style="idx > 0 ? 'border-top: 1px solid rgba(0,0,0,0.08)' : ''"
        >
          <span style="flex: 1">{{ formatarFaixaIntervalo(faixa) }}</span>
          <span class="font-weight-bold" style="color: #1976d2">{{ faixa.crescimento }}%</span>
        </div>
      </div>
    </div>

    <p class="caption grey--text ma-0 mt-2" style="line-height: 1.4">
      Meses sem realizado não entram no divisor da média. A meta é arredondada para cima e nunca fica abaixo do piso.
      <template v-if="tipoCrescimentoAtivo === 2">O crescimento de cada participante depende da própria média dele.</template>
    </p>

    <ModalFaixasCrescimento
      v-model="modalFaixasAberto"
      :faixas="form.faixasCrescimento"
      :piso="form.valorMinimoMeta"
      @save="salvarFaixas"
    />
  </div>
</template>

<script>
import { normalizarMeses } from "./seletorOrigemMetaUtils";
import { pendenciasFaixasCrescimento, calcularFaixasEfetivas } from "./faixasCrescimentoUtils";
import ModalFaixasCrescimento from "./ModalFaixasCrescimento.vue";

// Este componente cuida apenas do bloco "Parametros da Media" (meses de
// referencia, crescimento esperado e piso), exibido em largura cheia quando
// form.origemMeta === 2. Os seletores de Origem da meta do colaborador e de
// Origem do bonus do colaborador ficam em SubEtapaCalculoModelo.vue, na
// fileira flex do bloco "Meta do Participante".
export default {
  name: "SeletorOrigemMeta",

  components: { ModalFaixasCrescimento },

  props: {
    form: { type: Object, required: true }
  },

  data() {
    return {
      // Task 15: "campo tocado" e estado local de UI (nao vai pro form/payload).
      // O componente inteiro e desmontado pelo v-if em form.usaMetaColaborador
      // no pai (SubEtapaCalculoModelo) sempre que o switch desliga, entao o
      // reset ao religar e automatico — nao precisa de logica extra alem do
      // valor inicial false. Alternar so form.origemMeta (sem desligar o
      // switch) nao desmonta este componente, entao pisoTocado persiste
      // nesse caso — mesmo comportamento aceito para valorBonusColaboradorTocado
      // ao alternar so a origem do bonus, sem desligar o switch.
      pisoTocado: false,
      modalFaixasAberto: false,
      tiposCrescimento: [
        { value: 1, label: "Fixo" },
        { value: 2, label: "Progressivo por faixa" }
      ]
    };
  },

  computed: {
    // Nao editavel de proposito: a base da media espelha sempre a origem
    // do objetivo (Veiculo/Cota), para nunca divergir do que a campanha
    // mede como realizado. Ver payloadMetaExtras.montarExtrasMeta.
    textoBaseMedia() {
      return this.form.origemConteudo === 2
        ? "Cotas P1 de consórcio (quantidade) — mesma fonte do realizado"
        : "Vendas de veículo (quantidade) — mesma fonte do realizado";
    },

    erroPiso() {
      if (this.form.origemMeta !== 2) return "";
      const piso = parseFloat(this.form.valorMinimoMeta);
      if (!(piso > 0)) return "Informe um piso maior que zero";
      return "";
    },

    // Ultimos 12 meses fechados a partir do mes anterior ao atual.
    // Mes em aberto produz media errada, entao nao entra na lista.
    opcoesMeses() {
      const nomes = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
      const hoje = new Date();
      const lista = [];
      for (let i = 1; i <= 12; i++) {
        const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
        const ano = d.getFullYear();
        const mes = d.getMonth() + 1;
        lista.push({
          label: `${nomes[d.getMonth()]}/${ano}`,
          valor: { ano_ref: ano, mes_ref: mes }
        });
      }
      return lista;
    },

    // Default 1 (Fixo): metas antigas e novas sem o campo explicito se
    // comportam como sempre se comportaram.
    tipoCrescimentoAtivo() {
      return Number(this.form.tipoCrescimentoMedia) || 1;
    },

    pendenciasFaixas() {
      return pendenciasFaixasCrescimento(this.form.faixasCrescimento, this.form.valorMinimoMeta);
    },

    faixasEfetivas() {
      return calcularFaixasEfetivas(this.form.faixasCrescimento);
    },

    // So exibido quando falta algo (ver v-if no template) — quando esta
    // completo, a listagem inline das faixas ja responde por si so.
    resumoFaixas() {
      return `Falta: ${this.pendenciasFaixas.join(", ")}`;
    }
  },

  methods: {
    setTipoCrescimento(val) {
      this.$set(this.form, "tipoCrescimentoMedia", val);
      this.$emit("change-parametros");
    },

    salvarFaixas(faixas) {
      this.$set(this.form, "faixasCrescimento", faixas);
      this.$emit("change-parametros");
    },

    // Recebe uma faixa "efetiva" (calcularFaixasEfetivas: deEfetivo/ateEfetivo),
    // nao a faixa crua digitada — ateEfetivo vem do "de" da proxima faixa,
    // nao do rotulo que a pessoa escreveu (ver faixasCrescimentoUtils.js).
    formatarFaixaIntervalo(faixa) {
      const semTeto = faixa.ateEfetivo == null;
      return semTeto ? `A partir de ${faixa.deEfetivo}` : `De ${faixa.deEfetivo} até menos de ${faixa.ateEfetivo}`;
    },

    setMeses(valor) {
      // item-value="valor" (sem return-object) ja entrega Array<{ano_ref, mes_ref}>;
      // normalizarMeses so garante o shape e a ordem cronologica, com defesa
      // contra o item completo caso o binding do v-select mude no futuro.
      this.$set(this.form, "mesesReferencia", normalizarMeses(valor));
      this.$emit("change-parametros");
    },

    setParametro(campo, valor) {
      this.$set(this.form, campo, valor);
      this.$emit("change-parametros");
    }
  }
};
</script>
